import { create } from "zustand";
import type {
  SavedSearchSchema,
  SearchRunSchema,
  SearchRunSchemaExternalPhase,
} from "@/lib/api/model";

/**
 * Single source of truth for the run phase of every saved search the client
 * knows about. Fed from `GET /searches/{id}` / `GET /projects/{id}/searches`
 * payloads (`hydrateSearch` / `hydrateList`) and from the project-level SSE
 * stream (`applyEvent`). Components read phase/progress from here rather than
 * from cached query data so both feeds converge on one value per search.
 *
 * Run ordering: the backend never emits a terminal event for a run that gets
 * superseded by a re-run, so a run id we have not seen before is treated as
 * the newer run and the previously tracked id is remembered as superseded.
 * Late events (or stale cache payloads) for a superseded id are dropped, and
 * so is anything that would move a run already in a terminal phase back to a
 * running one. `run.cancelled` is the exception to "unknown id is newer": a
 * cancel only ever applies to the run we are tracking (the backend cancels
 * the old run when a re-run supersedes it), so one for another id, or for a
 * search the store does not track at all, is dropped.
 */

export type RunPhase = SearchRunSchemaExternalPhase;

export interface RunState {
  runId: string;
  phase: RunPhase;
  progress: { evaluated: number; target: number } | null;
  degradedReasons: string[];
  error: { code: string; message: string | null } | null;
  totalCount: number;
  /** Profiles the ranking phase could not evaluate; 0 until the API says otherwise. */
  evalFailedCount: number;
  /** ISO timestamp the run started, when the payload carries one. */
  startedAt: string | null;
  latestReadyRunId: string | null;
}

export interface RunEvent {
  type: "run.phase" | "run.ready" | "run.failed" | "run.cancelled";
  search_id: string;
  run_id: string;
  external_phase: RunPhase;
  progress: { evaluated: number; target: number } | null;
  degraded_reasons: string[];
  total_count: number;
  error: { code: string; message: string | null } | null;
  eval_failed_count?: number;
}

interface SearchRunsState {
  runs: Record<string, RunState>;
  /** search_id → run_id of the last READY run the user has looked at. */
  seenReady: Record<string, string>;
  /** search_id → run ids replaced by a newer run (most recent last, max 5). */
  supersededRunIds: Record<string, string[]>;
  /** The project run stream is open; set by the dashboard layout's mount. */
  streamConnected: boolean;
  /**
   * Project the open stream covers (null while disconnected). Runs for a
   * search in another project do not reach this stream, so readers compare
   * it with the search's own project before trusting `streamConnected`.
   */
  streamProjectId: string | null;
  /**
   * Searches with a run this client started (a manual run or an agent
   * `apply_filters`). The project stream carries every member's runs; only
   * these toast when they finish.
   */
  ownRuns: Record<string, true>;
  hydrateSearch: (search: SavedSearchSchema) => void;
  hydrateList: (searches: SavedSearchSchema[]) => void;
  applyEvent: (event: RunEvent) => void;
  markSeen: (searchId: string) => void;
  setStreamConnected: (connected: boolean, projectId?: string | null) => void;
  trackOwnRun: (searchId: string) => void;
  /** Forget an own run once it finished; true when it was one. */
  releaseOwnRun: (searchId: string) => boolean;
}

const RUNNING: ReadonlySet<RunPhase> = new Set(["searching", "ranking"]);
export const isRunning = (phase: RunPhase | undefined) =>
  !!phase && RUNNING.has(phase);

const TERMINAL: ReadonlySet<RunPhase> = new Set([
  "ready",
  "failed",
  "cancelled",
]);
export const isTerminal = (phase: RunPhase | undefined) =>
  !!phase && TERMINAL.has(phase);

const MAX_SUPERSEDED = 5;

/** Coarse count only; older payloads (SSE replays) may omit it. */
function evalFailedCountOf(run: { eval_failed_count?: number }): number {
  return run.eval_failed_count ?? 0;
}

function fromSchema(
  search: SavedSearchSchema,
  run: SearchRunSchema | null | undefined,
): RunState | null {
  if (!run) return null;
  return {
    runId: run.id,
    phase: run.external_phase,
    progress: run.progress ?? null,
    degradedReasons: run.degraded_reasons ?? [],
    error: run.error
      ? { code: run.error.code, message: run.error.message ?? null }
      : null,
    totalCount: run.total_count ?? 0,
    evalFailedCount: evalFailedCountOf(run),
    startedAt: run.started_at ?? null,
    latestReadyRunId: search.latest_ready_run_id ?? null,
  };
}

type RunsSlice = Pick<SearchRunsState, "runs" | "supersededRunIds" | "seenReady">;

/**
 * Merge `next` into the slice for `searchId`, applying the run-ordering
 * rules. `source` distinguishes SSE events (always fresh for their run) from
 * hydrates (possibly a stale cache payload), and a cancel event from the
 * rest (it only ever updates the run already tracked, never introduces one).
 */
function mergeRun(
  slice: RunsSlice,
  searchId: string,
  next: RunState,
  source: "hydrate" | "event" | "cancel",
): RunsSlice {
  const superseded = slice.supersededRunIds[searchId] ?? [];
  if (superseded.includes(next.runId)) return slice;

  const current = slice.runs[searchId];
  // A cancel for a run we are not tracking is stale or foreign: either the
  // re-run that superseded it is already in the store, or the search never
  // entered the store in this session. Neither should conjure up a run.
  if (source === "cancel" && current?.runId !== next.runId) return slice;

  let supersededRunIds = slice.supersededRunIds;
  if (current && current.runId !== next.runId) {
    supersededRunIds = {
      ...supersededRunIds,
      [searchId]: [...superseded, current.runId].slice(-MAX_SUPERSEDED),
    };
  } else if (current && isTerminal(current.phase) && isRunning(next.phase)) {
    // Same run: a stale hydrate, or a late `run.phase` event delivered after
    // the terminal one. Either way the run already finished.
    return slice;
  }

  const sameRun = !!current && current.runId === next.runId;
  const merged: RunState = {
    ...next,
    totalCount: next.totalCount ?? current?.totalCount ?? 0,
    startedAt: next.startedAt ?? (sameRun ? current.startedAt : null),
    latestReadyRunId:
      next.phase === "ready"
        ? next.runId
        : (next.latestReadyRunId ?? current?.latestReadyRunId ?? null),
  };

  // A search that first enters the store from a payload already READY was
  // ready before this session started (page load, project switch): there is
  // nothing new for the user to notice, so the dot only appears for runs that
  // finish while they are here (`run.ready` events).
  let seenReady = slice.seenReady;
  if (
    !current &&
    source === "hydrate" &&
    merged.phase === "ready" &&
    merged.latestReadyRunId
  ) {
    seenReady = { ...seenReady, [searchId]: merged.latestReadyRunId };
  }

  return {
    runs: { ...slice.runs, [searchId]: merged },
    supersededRunIds,
    seenReady,
  };
}

function fromEvent(event: RunEvent): RunState {
  return {
    runId: event.run_id,
    phase: event.external_phase,
    progress: event.progress,
    degradedReasons: event.degraded_reasons ?? [],
    error: event.error,
    totalCount: event.total_count,
    evalFailedCount: evalFailedCountOf(event),
    // Events do not carry the start time; `mergeRun` keeps the hydrated one.
    startedAt: null,
    latestReadyRunId: null,
  };
}

export const useSearchRunsStore = create<SearchRunsState>()((set, get) => ({
  runs: {},
  seenReady: {},
  supersededRunIds: {},
  streamConnected: false,
  streamProjectId: null,
  ownRuns: {},
  hydrateSearch: (search) => {
    const next = fromSchema(search, search.latest_run);
    if (!next) return;
    set((s) => mergeRun(s, search.id, next, "hydrate"));
  },
  hydrateList: (searches) => {
    set((s) => {
      let slice: RunsSlice = s;
      for (const search of searches) {
        const next = fromSchema(search, search.latest_run);
        if (next) slice = mergeRun(slice, search.id, next, "hydrate");
      }
      return slice;
    });
  },
  applyEvent: (event) => {
    const source = event.type === "run.cancelled" ? "cancel" : "event";
    set((s) => mergeRun(s, event.search_id, fromEvent(event), source));
  },
  markSeen: (searchId) => {
    const run = get().runs[searchId];
    if (!run?.latestReadyRunId) return;
    const readyRunId = run.latestReadyRunId;
    set((s) => ({ seenReady: { ...s.seenReady, [searchId]: readyRunId } }));
  },
  setStreamConnected: (connected, projectId = null) => {
    const streamProjectId = connected ? projectId : null;
    const s = get();
    if (
      s.streamConnected === connected &&
      s.streamProjectId === streamProjectId
    ) {
      return;
    }
    set({ streamConnected: connected, streamProjectId });
  },
  trackOwnRun: (searchId) => {
    if (get().ownRuns[searchId]) return;
    set((s) => ({ ownRuns: { ...s.ownRuns, [searchId]: true } }));
  },
  releaseOwnRun: (searchId) => {
    if (!get().ownRuns[searchId]) return false;
    set((s) => {
      const ownRuns = { ...s.ownRuns };
      delete ownRuns[searchId];
      return { ownRuns };
    });
    return true;
  },
}));

export const selectRun =
  (searchId: string | null | undefined) => (s: SearchRunsState) =>
    searchId ? s.runs[searchId] : undefined;

/**
 * Clear all tracked runs, seen markers, superseded ids, own runs and the
 * stream state. Called on sign-out (the next user must not inherit this
 * session's run state) and by tests.
 */
export function resetSearchRunsStore(): void {
  useSearchRunsStore.setState({
    runs: {},
    seenReady: {},
    supersededRunIds: {},
    streamConnected: false,
    streamProjectId: null,
    ownRuns: {},
  });
}
